import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const US_CENTER = { lat: 39.5, lng: -98.35 };

async function geocodeQuery(query) {
  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: query }, (results, status) => {
      const first = results?.[0];

      if (status === "OK" && first?.geometry?.location) {
        const loc = first.geometry.location;

        resolve({
          center: { lat: loc.lat(), lng: loc.lng() },
          viewport: first.geometry.viewport || null,
          bounds: first.geometry.bounds || null,
        });
      } else {
        resolve(null);
      }
    });
  });
}

const COUNTY_TARGET_ZOOM = 11;
const STATE_INSIDE_ZOOM = 7;

async function resolveAgentCenter(access) {
  if (!access?.length) return { center: US_CENTER, zoom: 4, viewport: null };

  const county = access[0].county_name;
  const stateCode = access[0].state_code || access[0].state;

  if (county && stateCode) {
    const result = await geocodeQuery(`${county} County, ${stateCode}, USA`);
    // Prefer bounds (precise admin-area bbox) over viewport (looser display rect).
    if (result) return { center: result.center, zoom: COUNTY_TARGET_ZOOM, viewport: result.bounds || result.viewport };
  }

  if (stateCode) {
    const result = await geocodeQuery(`${stateCode}, USA`);
    if (result) return { center: result.center, zoom: STATE_INSIDE_ZOOM, viewport: result.bounds || result.viewport };
  }

  return { center: US_CENTER, zoom: 4, viewport: null };
}

function polygonToGeoJSON(polygon) {
  const path = polygon.getPath().getArray();

  const coords = path.map((p) => [p.lng(), p.lat()]);

  coords.push(coords[0]);

  return {
    type: "Polygon",
    coordinates: [coords],
  };
}

function rectangleToGeoJSON(rectangle) {
  const bounds = rectangle.getBounds();
  const southwest = bounds.getSouthWest();
  const northeast = bounds.getNorthEast();
  const northwest = new window.google.maps.LatLng(northeast.lat(), southwest.lng());
  const southeast = new window.google.maps.LatLng(southwest.lat(), northeast.lng());

  return {
    type: "Polygon",
    coordinates: [[
      [southwest.lng(), southwest.lat()],
      [southeast.lng(), southeast.lat()],
      [northeast.lng(), northeast.lat()],
      [northwest.lng(), northwest.lat()],
      [southwest.lng(), southwest.lat()],
    ]],
  };
}

export default function MapDrawingFilter({
  onPolygonChange,
  access,
}) {
  const mapRef = useRef(null);
  const shapeRef = useRef(null);
  const draftShapeRef = useRef(null);
  const mapClickListenerRef = useRef(null);
  const mapDoubleClickListenerRef = useRef(null);
  const shapeListenersRef = useRef([]);
  const drawingModeRef = useRef(null);
  const firstRectanglePointRef = useRef(null);

  const [hasShape, setHasShape] = useState(false);
  const [drawingMode, setDrawingMode] = useState(null);

  const removeMapClickListener = useCallback(() => {
    if (mapClickListenerRef.current) {
      window.google.maps.event.removeListener(mapClickListenerRef.current);
      mapClickListenerRef.current = null;
    }
    if (mapDoubleClickListenerRef.current) {
      window.google.maps.event.removeListener(mapDoubleClickListenerRef.current);
      mapDoubleClickListenerRef.current = null;
    }
  }, []);

  const removeShapeListeners = useCallback(() => {
    shapeListenersRef.current.forEach((listener) =>
      window.google.maps.event.removeListener(listener)
    );
    shapeListenersRef.current = [];
  }, []);

  const removeDraftShape = useCallback(() => {
    if (draftShapeRef.current) {
      draftShapeRef.current.setMap(null);
      draftShapeRef.current = null;
    }
    firstRectanglePointRef.current = null;
  }, []);

  const emitShape = useCallback((shape, type) => {
    const geoJson = type === "rectangle"
      ? rectangleToGeoJSON(shape)
      : polygonToGeoJSON(shape);

    onPolygonChange(JSON.stringify(geoJson));
  }, [onPolygonChange]);

  const finishDrawing = useCallback(() => {
    const draftShape = draftShapeRef.current;
    const mode = drawingModeRef.current;

    if (!draftShape || !mode) return;
    if (mode === "polygon" && draftShape.getPath().getLength() < 3) return;
    if (mode === "rectangle" && !draftShape.getBounds()) return;

    removeMapClickListener();
    if (shapeRef.current) shapeRef.current.setMap(null);
    draftShapeRef.current = null;
    firstRectanglePointRef.current = null;
    shapeRef.current = draftShape;
    drawingModeRef.current = null;
    setDrawingMode(null);
    setHasShape(true);
    removeShapeListeners();
    const updateShape = () => emitShape(shapeRef.current, mode);
    const eventName = mode === "rectangle" ? "bounds_changed" : "path_changed";
    shapeListenersRef.current.push(
      window.google.maps.event.addListener(shapeRef.current, eventName, updateShape)
    );
    emitShape(shapeRef.current, mode);
  }, [emitShape, removeMapClickListener, removeShapeListeners]);

  const updateRectangle = useCallback((point) => {
    const firstPoint = firstRectanglePointRef.current;
    if (!firstPoint || !mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds(firstPoint, point);
    if (!draftShapeRef.current) {
      draftShapeRef.current = new window.google.maps.Rectangle({
        map: mapRef.current,
        bounds,
        fillColor: "#3b82f6",
        fillOpacity: 0.2,
        strokeColor: "#1d4ed8",
        strokeWeight: 2,
        editable: true,
        draggable: true,
      });
    } else {
      draftShapeRef.current.setBounds(bounds);
    }
  }, []);

  const startDrawing = useCallback((mode) => {
    if (!mapRef.current) return;

    removeMapClickListener();
    removeDraftShape();
    if (shapeRef.current) {
      shapeRef.current.setMap(null);
      shapeRef.current = null;
      setHasShape(false);
      onPolygonChange("");
    }

    drawingModeRef.current = mode;
    setDrawingMode(mode);

    mapClickListenerRef.current = window.google.maps.event.addListener(
      mapRef.current,
      "click",
      (event) => {
        if (drawingModeRef.current === "rectangle") {
          if (!firstRectanglePointRef.current) {
            firstRectanglePointRef.current = event.latLng;
          } else {
            updateRectangle(event.latLng);
          }
          return;
        }

        if (!draftShapeRef.current) {
          draftShapeRef.current = new window.google.maps.Polygon({
            map: mapRef.current,
            path: [],
            fillColor: "#3b82f6",
            fillOpacity: 0.2,
            strokeColor: "#1d4ed8",
            strokeWeight: 2,
            editable: true,
            draggable: true,
          });
        }

        const path = draftShapeRef.current.getPath();
        const lastPoint = path.getAt(path.getLength() - 1);
        if (!lastPoint || lastPoint.lat() !== event.latLng.lat() || lastPoint.lng() !== event.latLng.lng()) {
          path.push(event.latLng);
        }
      }
    );
    mapDoubleClickListenerRef.current = window.google.maps.event.addListener(
      mapRef.current,
      "dblclick",
      finishDrawing
    );
  }, [finishDrawing, onPolygonChange, removeDraftShape, removeMapClickListener, updateRectangle]);

  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
    },
    []
  );

  useEffect(() => {
    return () => {
      removeMapClickListener();
      removeDraftShape();
      removeShapeListeners();

      if (shapeRef.current) {
        shapeRef.current.setMap(null);
        shapeRef.current = null;
      }
    };
  }, [removeDraftShape, removeMapClickListener, removeShapeListeners]);

  useEffect(() => {
    if (!mapRef.current || !access?.length) return;

    const isAlive = { current: true };

    resolveAgentCenter(access).then(({ center, zoom, viewport }) => {
      requestAnimationFrame(() => {
        const map = mapRef.current;
        if (!map || !isAlive.current) return;

        if (viewport) {
          // Fit exactly to the county/state boundary returned by the geocoder
          map.fitBounds(viewport);
        } else {
          map.setCenter(center);
          map.setZoom(zoom);
        }
      });
    }
    );

    return () => { isAlive.current = false; };
  }, [access]);

  const clearShape = useCallback(() => {
    removeMapClickListener();
    removeDraftShape();
    removeShapeListeners();
    drawingModeRef.current = null;
    setDrawingMode(null);

    if (shapeRef.current) {
      shapeRef.current.setMap(null);
      shapeRef.current = null;
    }

    setHasShape(false);

    onPolygonChange("");
  }, [onPolygonChange, removeDraftShape, removeMapClickListener, removeShapeListeners]);

  return (
    <div className="relative h-full w-full">
      <GoogleMap
      mapContainerStyle={MAP_CONTAINER_STYLE}
      center={US_CENTER}
      zoom={4}
      onLoad={onMapLoad}
        options={{
          streetViewControl: true, // little yellow person
          mapTypeControl: true, // Map / Satellite toggle
          fullscreenControl: true,
          zoomControl: true,

          mapTypeControlOptions: {
            style:
              window.google.maps.MapTypeControlStyle
                .HORIZONTAL_BAR,
            position:
              window.google.maps.ControlPosition
                .TOP_RIGHT,
          },

          streetViewControlOptions: {
            position:
              window.google.maps.ControlPosition
                .RIGHT_BOTTOM,
          },

          fullscreenControlOptions: {
            position:
              window.google.maps.ControlPosition
                .RIGHT_TOP,
          },

          zoomControlOptions: {
            position:
              window.google.maps.ControlPosition
                .RIGHT_CENTER,
          },
        }}
      />

      <div className="absolute top-3 left-3 z-10 flex gap-2 rounded-md bg-white p-2 shadow-md">
        <button type="button" onClick={() => startDrawing("polygon")} className="rounded border border-zinc-300 px-3 py-2 text-xs font-semibold text-zinc-700 hover:border-zinc-900 hover:text-zinc-950">
          Draw Polygon
        </button>
        <button type="button" onClick={() => startDrawing("rectangle")} className="rounded border border-zinc-300 px-3 py-2 text-xs font-semibold text-zinc-700 hover:border-zinc-900 hover:text-zinc-950">
          Draw Rectangle
        </button>
        {drawingMode && (
          <button type="button" onClick={finishDrawing} className="rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700">
            Done
          </button>
        )}
        {(hasShape || drawingMode) && (
          <button type="button" onClick={clearShape} className="rounded border border-zinc-300 px-3 py-2 text-xs font-semibold text-zinc-700 hover:border-zinc-900 hover:text-zinc-950">
            Clear
          </button>
        )}
      </div>
    </div>
  );
}